import { Check, Copy, Cpu } from "lucide-react";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";

function Message({ message, timestamp, isLast }) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async text => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  // Helper functions for smarter code detection
  const isFilePath = text => {
    const trimmed = text.trim();
    return (
      /^[a-zA-Z0-9_-]+\/[a-zA-Z0-9_/.-]+\.[a-zA-Z0-9]+$/.test(trimmed) ||
      /^[a-zA-Z0-9_-]+\\[a-zA-Z0-9_\\.-]+\.[a-zA-Z0-9]+$/.test(trimmed) ||
      trimmed.includes("/src/") ||
      trimmed.includes("/components/") ||
      trimmed.includes("/utils/") ||
      trimmed.includes("/pages/") ||
      trimmed.includes("/css/") ||
      trimmed.includes(".js") ||
      trimmed.includes(".jsx") ||
      trimmed.includes(".ts") ||
      trimmed.includes(".tsx") ||
      trimmed.includes(".css") ||
      trimmed.includes(".json")
    );
  };

  const isSimpleVariable = text => {
    const trimmed = text.trim();
    return (
      trimmed.length < 30 &&
      !trimmed.includes("\n") &&
      !trimmed.includes(";") &&
      !trimmed.includes("{") &&
      !trimmed.includes("(") &&
      !trimmed.includes("=") &&
      !trimmed.includes("/") &&
      /^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(trimmed)
    );
  };

  const isShortCodeSnippet = text => {
    const trimmed = text.trim();
    return (
      trimmed.length < 100 &&
      !trimmed.includes("\n") &&
      (trimmed.includes("()") ||
        trimmed.includes("{}") ||
        trimmed.includes("[]") ||
        trimmed.includes("=") ||
        trimmed.includes(";"))
    );
  };

  const getInlineCodeClass = text => {
    if (isFilePath(text)) {
      return "chat-file-path";
    }
    if (isSimpleVariable(text)) {
      return "chat-variable";
    }
    if (isShortCodeSnippet(text)) {
      return "chat-code-snippet";
    }
    return "chat-inline-code";
  };

  // Custom markdown components with T3 styling and smart code detection
  const markdownComponents = {
    // Enhanced code detection with smarter block vs inline logic
    code: ({ node, inline, className, children, ...props }) => {
      const codeContent = String(children).replace(/\n$/, "");
      const match = /language-(\w+)/.exec(className || "");
      const language = match ? match[1] : "text";

      // Determine if this should be a block or inline
      const hasMultipleLines = codeContent.includes("\n");
      const isLongContent = codeContent.length > 80;
      const hasComplexSyntax = /[{}();=].*[{}();=]/.test(codeContent) || codeContent.split("\n").length > 2;

      // Force inline for certain patterns
      const shouldForceInline =
        inline ||
        (!hasMultipleLines && !isLongContent && !hasComplexSyntax) ||
        isFilePath(codeContent) ||
        isSimpleVariable(codeContent) ||
        isShortCodeSnippet(codeContent);

      if (!shouldForceInline && (hasMultipleLines || isLongContent || hasComplexSyntax)) {
        return (
          <div className="chat-code-block-container">
            <div className="chat-code-header">
              <span className="chat-code-language">{language}</span>
              <button onClick={() => copyToClipboard(codeContent)} className="chat-code-copy" title="Copy code">
                <Copy size={14} />
              </button>
            </div>
            <pre className="chat-code-content">
              <code className={className} {...props}>
                {children}
              </code>
            </pre>
          </div>
        );
      }

      // Enhanced inline code with smart styling
      const inlineClass = getInlineCodeClass(codeContent);
      return (
        <code className={inlineClass} {...props}>
          {children}
        </code>
      );
    },

    // Headings with T3 styling
    h1: ({ children }) => <h1 className="chat-heading chat-heading-1">{children}</h1>,
    h2: ({ children }) => <h2 className="chat-heading chat-heading-2">{children}</h2>,
    h3: ({ children }) => <h3 className="chat-heading chat-heading-3">{children}</h3>,
    h4: ({ children }) => <h4 className="chat-heading chat-heading-4">{children}</h4>,
    h5: ({ children }) => <h5 className="chat-heading chat-heading-5">{children}</h5>,
    h6: ({ children }) => <h6 className="chat-heading chat-heading-6">{children}</h6>,

    // Paragraphs
    p: ({ children }) => <p className="chat-paragraph">{children}</p>,

    // Lists
    ul: ({ children }) => <ul className="chat-list chat-list-unordered">{children}</ul>,
    ol: ({ children }) => <ol className="chat-list chat-list-ordered">{children}</ol>,
    li: ({ children }) => <li className="chat-list-item">{children}</li>,

    // Emphasis
    strong: ({ children }) => <strong className="chat-strong">{children}</strong>,
    em: ({ children }) => <em className="chat-emphasis">{children}</em>,

    // Links
    a: ({ href, children }) => (
      <a href={href} className="chat-link" target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),

    // Blockquotes
    blockquote: ({ children }) => <blockquote className="chat-blockquote">{children}</blockquote>,

    // Tables
    table: ({ children }) => (
      <div className="chat-table-container">
        <table className="chat-table">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="chat-table-head">{children}</thead>,
    tbody: ({ children }) => <tbody className="chat-table-body">{children}</tbody>,
    tr: ({ children }) => <tr className="chat-table-row">{children}</tr>,
    th: ({ children }) => <th className="chat-table-header">{children}</th>,
    td: ({ children }) => <td className="chat-table-cell">{children}</td>,

    // Horizontal rule
    hr: () => <hr className="chat-divider" />,
  };

  // USER MESSAGE
  if (message.role === "user") {
    return (
      <div className="chat-user-message-wrapper">
        <div className="chat-user-bubble">
          <div className="chat-user-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw, rehypeSanitize]}
              components={markdownComponents}
            >
              {message.content}
            </ReactMarkdown>
          </div>

          <button
            className="chat-user-copy-btn"
            onClick={() => copyToClipboard(message.content)}
            title={copied ? "Copied!" : "Copy message"}
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
          </button>
        </div>
      </div>
    );
  }

  // ASSISTANT MESSAGE
  return (
    <div className="chat-assistant-message-wrapper">
      <div className="chat-assistant-bubble">
        <div className="chat-assistant-content">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, rehypeSanitize]}
            components={markdownComponents}
          >
            {message.content}
          </ReactMarkdown>
        </div>

        <div className="chat-assistant-actions">
          <button
            className="chat-assistant-copy-btn"
            onClick={() => copyToClipboard(message.content)}
            title={copied ? "Copied!" : "Copy response"}
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
          </button>

          <div className="chat-assistant-metadata">
            <span className="chat-metadata-item">
              <Cpu size={12} />
              {Math.floor(Math.random() * 200) + 100} tokens
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Message;
